import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { apiFetch } from "@/modules/shared/lib/api-client";
import { usePeriod } from "@/modules/shared/dashboard/PeriodContext";
import { useBatchKpi } from "@/modules/shared/dashboard/BatchKpiContext";

type KpiResponse = { value: number; deltaPct: number | null; deltaLabel: string; sparkline?: number[] };
type Tone = "ink" | "blue" | "amber" | "red" | "green";

type Props = {
  title: string;
  endpoint: string;
  icon: LucideIcon;
  href: string;
  tone?: Tone;
  /** Optional formatter for the primary value. Defaults to String(value). */
  valueFormatter?: (value: number) => string;
};

function Sparkline({ values }: { values: number[] }) {
  if (!values || values.length < 2) return null;
  const w = 80;
  const h = 24;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = w / (values.length - 1);
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`)
    .join(" ");
  const last = values[values.length - 1];
  const first = values[0];
  const stroke = last >= first ? "var(--green, #10b981)" : "var(--red, #ef4444)";
  return (
    <svg width={w} height={h} className="mt-1" aria-hidden>
      <polyline fill="none" stroke={stroke} strokeWidth="1.5" points={points} />
    </svg>
  );
}

export function KpiWidget({ title, endpoint, icon: Icon, href, tone = "ink", valueFormatter }: Props) {
  const period = usePeriod();
  const url = `${endpoint}?period=${period}`;

  // Read from batch cache when a BatchKpiProvider wraps this widget.
  // undefined → no provider (use per-widget fetch)
  // null      → provider present but still loading
  // object    → resolved value
  const batchEntry = useBatchKpi(endpoint);
  const useBatch = batchEntry !== undefined;

  const { data: fetchedData, isLoading: fetchLoading } = useQuery<KpiResponse>({
    queryKey: ["widget", endpoint, period],
    queryFn: () => apiFetch<KpiResponse>(url),
    enabled: !useBatch,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const data = useBatch ? (batchEntry ?? undefined) : fetchedData;
  const isLoading = useBatch ? batchEntry === null : fetchLoading;

  const value = data?.value ?? 0;
  const display = isLoading ? "…" : (valueFormatter ? valueFormatter(value) : String(value));
  const deltaPct = data?.deltaPct;
  const deltaLabel = data?.deltaLabel ?? "";

  // `flex flex-col` + `min-h-[140px]` keeps every KPI tile at the same
  // visual height regardless of whether it has a sparkline or trend badge.
  // Sparkline + delta sit at the bottom via `mt-auto` so cards stay balanced
  // even when one tile has more data than another.
  return (
    <Link
      to={href}
      className="ol-kpi block no-underline flex flex-col min-h-[140px]"
      style={{ color: "inherit" }}
    >
      <div className="ol-kpi-top">
        <div className={`ol-kpi-ic ${tone}`}><Icon /></div>
        <span className="ol-kpi-l">{title}</span>
      </div>
      <div className="ol-kpi-v">{display}</div>
      <div className="mt-auto">
        {data?.sparkline && data.sparkline.length >= 2 && <Sparkline values={data.sparkline} />}
        {deltaPct != null && deltaPct !== 0 && (
          <div className={`ol-kpi-delta ${deltaPct > 0 ? "up" : "down"}`}>
            {deltaPct > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {deltaPct > 0 ? "+" : ""}{deltaPct.toFixed(1)}% <span style={{ color: "inherit", opacity: 0.7 }}>{deltaLabel}</span>
          </div>
        )}
      </div>
    </Link>
  );
}
